import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PlaceHoldDialog } from "./PlaceHoldDialog";
import {
  InterfaceType,
  PlaceHoldInfoImpl,
  PlaceHoldPayloadItem,
  placeHoldRequest,
} from "../services/Holds";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { usePatronLocation } from "../pages/resource/usePatronLocation";
import { useLocationConfiguration } from "../LocationConfiguration";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { getResourceById } from "../pages/resource/getResourceById";
import { useToast } from "@tlc/ui/toast";
import { useFailedHoldMessage } from "../useFailedHoldMessage";
import { PlaceHoldResponseDialog } from "./PlaceHoldResponseDialog";
import { useLanguage } from "../Language";
import { AccountHolder } from "../Account";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { useBranchLimits } from "../BranchLimits";
import { UserView } from "../AccountMenu";
import { useAnalytics } from "../../analytics/useGoogleAnalytics";
import { useDialogRegistry } from "@tlc/ui/dialog/DialogRegistry";
import { useDialog } from "@tlc/ui/dialog/useDialog";

interface PlaceHoldContextType {
  placeHold: (resource: ResourceDWR | ResourceView) => void;
}

export const PlaceHoldContext = createContext<PlaceHoldContextType | undefined>(
  undefined,
);

export const PlaceHoldProvider = ({ children }: React.PropsWithChildren) => {
  const { showToast } = useToast();
  const { registerDialog } = useDialogRegistry();
  const { open, openDialog, closeDialog } = useDialog();
  const { translateToken, translate } = useLanguage();
  const failedHoldMessage = useFailedHoldMessage();
  const { pickupLocation: pickupLocationId } = usePatronLocation();
  const { displayInPickupLocationIds, displayInCopiesBranchIds } =
    useBranchLimits();
  const { locationConfiguration } = useLocationConfiguration();
  const { trackPlaceHold } = useAnalytics();
  const { pacSettings } = locationConfiguration || {};
  const { userActionSettings } = pacSettings;
  const { allowItemLevelHolds, restrictTitleLevelHolds } =
    userActionSettings || {};
  const { displaySettings } = pacSettings || {};
  const { displaySelectPickupLocation } = displaySettings || {};

  const [resource, setResource] = useState<ResourceDWR>();
  const [placeHoldResponse, setPlaceHoldResponse] = React.useState<
    PlaceHoldInfoImpl[] | null
  >(null);

  const submitPlaceHoldRequest = useCallback(
    (payload: PlaceHoldPayloadItem[]) => {
      trackPlaceHold(
        payload[0]?.titleLevelHold ? "title" : "item",
        payload.length,
      );

      placeHoldRequest(payload, restrictTitleLevelHolds).then(
        ({ placeHoldInfos }) => {
          if (placeHoldInfos.length === 1) {
            const holdInfo = placeHoldInfos[0];
            const { success, message: token, guardiansBlocking } = holdInfo;
            showToast(
              success
                ? translateToken(token)
                : failedHoldMessage(token, guardiansBlocking),
              success ? "success" : "error",
            );
            if (success) {
              AccountHolder.fetchAccountSummaryAndPatron(true);
            }
            return;
          }

          const failureList = placeHoldInfos.filter(({ success }) => !success);
          const successList = placeHoldInfos.filter(({ success }) => success);

          if (successList.length > 0) {
            AccountHolder.fetchAccountSummaryAndPatron(true);
          }

          if (failureList.length === 0 && successList.length > 1) {
            showToast(translate("batchRequestSuccessMessage"), "success");
            return;
          }

          setPlaceHoldResponse(placeHoldInfos);
        },
      );
    },
    [
      trackPlaceHold,
      restrictTitleLevelHolds,
      showToast,
      translate,
      translateToken,
      failedHoldMessage,
    ],
  );

  const placeHold = useCallback(
    (res: ResourceDWR | ResourceView) => {
      LoginClientManager.promptIfNotLoggedIn(
        async (user: Readonly<UserView>) => {
          const issuingBranchId = user.issuingBranch.toString();
          const isPlaceHoldBlocked = (issuingBranchId: string) => {
            if (!restrictTitleLevelHolds && displaySelectPickupLocation) {
              return false;
            }

            if (restrictTitleLevelHolds) {
              // issuing branch must be included in pickup locations and copies when restrictTitleLevelHolds is enabled
              return (
                !displayInCopiesBranchIds.includes(issuingBranchId) ||
                !displayInPickupLocationIds.includes(issuingBranchId)
              );
            }

            // if patron's location is not part of pickup locations and we don't allow changing pickup location
            if (!displaySelectPickupLocation) {
              return !displayInPickupLocationIds.includes(issuingBranchId);
            }

            return false;
          };
          if (isPlaceHoldBlocked(issuingBranchId)) {
            showToast(translate("patronLocationHoldError"), "error");
            return;
          }

          const placeTitleLevelHoldWithoutPrompt =
            !allowItemLevelHolds &&
            (restrictTitleLevelHolds || !displaySelectPickupLocation);

          if (placeTitleLevelHoldWithoutPrompt) {
            submitPlaceHoldRequest([
              {
                pickupBranchId: issuingBranchId,
                bibliographicId: res.hostBibliographicId,
                titleLevelHold: true,
                serialItem: res.serial,
                downloadable: res.downloadable,
                interfaceType: InterfaceType.PAC,
              },
            ]);
            return;
          }

          const resource = await getResourceById(res.id);
          setResource(resource);
          openDialog();
        },
      );
    },
    [
      displayInCopiesBranchIds,
      displayInPickupLocationIds,
      restrictTitleLevelHolds,
      allowItemLevelHolds,
      displaySelectPickupLocation,
      showToast,
      translate,
      openDialog,
      submitPlaceHoldRequest,
    ],
  );

  const contextValue: PlaceHoldContextType = useMemo(
    () => ({
      placeHold,
    }),
    [placeHold],
  );

  const handleSubmit = useCallback(
    (payload: PlaceHoldPayloadItem[]) => {
      submitPlaceHoldRequest(payload);
      closeDialog();
    },
    [submitPlaceHoldRequest, closeDialog],
  );

  useEffect(() => {
    return registerDialog(() => {
      setPlaceHoldResponse(null);
      setResource(undefined);
    });
  }, [registerDialog, setPlaceHoldResponse, setResource]);

  return (
    <PlaceHoldContext.Provider value={contextValue}>
      <PlaceHoldDialog
        resource={resource}
        pickupLocationId={pickupLocationId}
        open={open}
        onClose={closeDialog}
        onSubmit={handleSubmit}
      ></PlaceHoldDialog>
      <PlaceHoldResponseDialog
        open={Boolean(placeHoldResponse)}
        response={placeHoldResponse}
        resource={resource}
        onClose={() => setPlaceHoldResponse(null)}
      />
      {children}
    </PlaceHoldContext.Provider>
  );
};

export function usePlaceHold(): PlaceHoldContextType {
  const ctx = useContext(PlaceHoldContext);
  if (ctx === undefined) {
    throw new Error("usePlaceHold must be used within a PlaceHoldProvider");
  }
  return ctx;
}
